import { configureAuth } from "app/Auth/configureAuth";
import { fetchUserInfo } from "app/Auth/fetchUserInfo";
import { useUserInfoStore } from "app/Auth/useUserInfoStore";
import { logoutFromPuzzel } from "app/PuzzelAuth/puzzelAuth";
import { hasPuzzelLoginInBrowser } from "app/PuzzelAuth/puzzelTokenStorage";
import {
  AuthSession,
  signInWithRedirect as cognitoSignInWithRedirect,
  signOut as cognitoSignOut,
  fetchAuthSession,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { isAxiosError } from "axios";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

export interface AuthContextType {
  authState: AuthState;
  hasErrored: boolean;
  error: Error | null;
  sessionExpired: boolean;
  subscribe: () => void;
  signInWithRedirect: () => void;
  signOut: () => void;
}

type AuthState = "loading" | "unauthenticated" | "unauthorized" | "authorized";

class UnauthenticatedError extends Error {
  constructor(message: string = "User is not authenticated") {
    super(message);
    this.name = "UnauthenticatedError";
  }
}

const isUnauthorizedError = (error: any): boolean =>
  isAxiosError(error) && error.response?.status === 403;

const getSession = async (): Promise<AuthSession> => {
  try {
    // fetchAuthSession will attempt to refresh tokens when they're expired
    // if a valid refresh token exists. forceRefresh: true forces this check
    // even if tokens appear valid in storage
    const session = await fetchAuthSession({ forceRefresh: true });
    const token = session.tokens?.accessToken;

    if (!token) {
      throw new UnauthenticatedError("No access token available");
    }

    return session;
  } catch {
    throw new UnauthenticatedError("Failed to refresh authentication session");
  }
};

const signInWithRedirect = async (): Promise<void> => {
  const trySignIn = async (): Promise<void> => {
    await cognitoSignInWithRedirect({ provider: { custom: "vygruppen" } });
  };

  try {
    await trySignIn();
  } catch (error: any) {
    // If the access token in local storage for some reason gets corrupted,
    // we attempt to recover by signing out the user first to reset the session.
    if (error.name === "UserAlreadyAuthenticatedException") {
      try {
        await cognitoSignOut();
        await trySignIn();
      } catch (err: any) {
        log(
          LogLevel.error,
          "signInWithRedirect",
          `Error when trying to sign-in user with already existing session`,
          err,
        );
      }
    } else {
      log(
        LogLevel.error,
        "signInWithRedirect",
        `Error when trying to sign-in user`,
        error,
      );
    }
  }
};

/**
 * Sign out user from all devices.
 *
 * Invalidates all refresh tokens issued to the user.
 * The user's current access and ID tokens will remain valid on other devices until the refresh token expires.
 */
const signOut = async (): Promise<void> => {
  // Shared shift monitors: don't let a Puzzel session carry over to the next Vy user on this
  // machine. Puzzel's redirect must complete first (own full-page navigation), then it lands on
  // /puzzel-logout-continue to finish signing out of Vy/Entra - chained rather than fired at the
  // same time as cognitoSignOut's own redirect, which would otherwise race it.
  // Checked browser-wide, not per tab: Puzzel tokens are per tab, so this tab may have none even
  // though another tab (and Puzzel ID's SSO cookie) is still logged in.
  if (hasPuzzelLoginInBrowser()) {
    logoutFromPuzzel();
    return;
  }

  try {
    await cognitoSignOut({ global: true });
  } catch (error: any) {
    log(LogLevel.error, "signOut", `Error when trying to sign-out user`, error);
  }
};

const useProvideAuth = (): AuthContextType => {
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [error, setError] = useState<Error | null>(null);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);

  const { setUserInfo, clearUserInfo } = useUserInfoStore();

  const handleError = (state: AuthState, err: Error) => {
    log(
      LogLevel.error,
      "AuthBoundary",
      `Unexpected error during authentication`,
      err,
    );

    setError(err);
    clearUserInfo();
    setAuthState(state);
  };

  const authenticate = async () => {
    try {
      await getSession();
      const user = await fetchUserInfo();
      setAuthState("authorized");
      setUserInfo(user);
      setError(null);
      setSessionExpired(false);
    } catch (err: any) {
      if (err instanceof UnauthenticatedError) {
        setAuthState("unauthenticated");
        setError(null);

        if (authState === "authorized") {
          setSessionExpired(true);
        }
        return;
      }

      if (isUnauthorizedError(err)) {
        handleError("unauthorized", err);
        return;
      }

      handleError("unauthenticated", err);
    }
  };

  const unsubscribe = useRef<() => void>(() => {});
  const subscribe = useRef(() => {
    const subscribeToAuthEvents = () =>
      Hub.listen(
        "auth",
        async ({ payload }) => {
          switch (payload.event) {
            case "signedIn":
              await authenticate();
              break;
            case "tokenRefresh_failure":
            case "signInWithRedirect_failure":
              log(
                LogLevel.warn,
                "AuthBoundary",
                `Received auth failure event: ${payload.event}`,
                payload.data?.error,
              );
              setAuthState("unauthenticated");
              setSessionExpired(true);
              break;
            case "signedOut":
              setAuthState("unauthenticated");
              clearUserInfo();
              setSessionExpired(false);
              break;

            case "signInWithRedirect":
            case "tokenRefresh":
            case "customOAuthState":
          }
        },
        "auth-boundary-listener",
      );

    const startAuth = async () => {
      try {
        await configureAuth();
        unsubscribe.current = subscribeToAuthEvents();
        await authenticate();
      } catch (err: any) {
        handleError("unauthenticated", err);
      }
    };

    void startAuth();
  });

  useEffect(() => {
    subscribe.current();
    return unsubscribe.current;
  }, []);

  return {
    authState,
    hasErrored: !!error,
    error,
    sessionExpired,
    subscribe: subscribe.current,
    signInWithRedirect,
    signOut,
  };
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const auth = useProvideAuth();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider context!");
  }
  return context;
};
